// @ts-strict-ignore
import * as React from 'react';
import {
    Box,
    ListItem,
    Chip,
    Button as MuiButton,
    TextField,
    selectClasses,
} from '@mui/material';
import type { SelectProps, MenuProps } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ArrowDownIcon } from '../icons';

export type SelectOption = { label: React.ReactNode; value: string };
export type SelectOptions = ReadonlyArray<SelectOption>;

export type MultipleSelectProps<T extends string | string[] = string[]> = {
    label?: React.ReactNode;
    isOpen: boolean;
    onOpen: () => void;
    onClose?: () => void;
    values?: T;
    options: SelectOptions;
    onChange?: SelectProps<T>['onChange'];
    handleClose: () => void;
    onDeleteValue?: (option: SelectOption) => void;
    children: React.ReactNode;
    MenuProps?: MenuProps;
    multiple?: boolean;
    renderValue?: (values: T) => React.ReactNode;
    noMargin?: boolean;
    placeholder?: React.ReactNode;
    hideCloseButton?: boolean;
};

export const MultipleSelect = React.forwardRef(
    <T extends string | string[] = string[]>(
        props: MultipleSelectProps<T>,
        ref,
    ) => {
        const {
            label,
            isOpen,
            onOpen,
            values,
            options,
            onChange,
            handleClose,
            onDeleteValue,
            children,
            MenuProps,
            multiple = true,
            renderValue,
            // TODO: We can remove this in the future after we change the `MultipleSelect` api to support `SingleSelect`.
            noMargin = false,
            hideCloseButton = false,
        } = props;

        // TODO: We have changes in this Component. We support the single select. Because of this, when the `multiple` prop is false,
        // then the passed `values` is a string not an array. We need to handle the "string" case.
        //
        // It renders the selected items via showing a Chip inside the text content of the Select field.
        function _renderValue(values: any) {
            if (values.length === 0) {
                return null;
            }

            return (
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {options
                        .filter((option) => values.indexOf(option.value) > -1)
                        .map((option) => (
                            <Chip
                                color="primary"
                                key={option.value}
                                label={option.label}
                                onMouseDown={(event) => {
                                    event.stopPropagation();
                                }}
                                onDelete={
                                    onDeleteValue
                                        ? () => {
                                              onDeleteValue(option);
                                          }
                                        : undefined
                                }
                            />
                        ))}
                </Box>
            );
        }

        return (
            <TextField
                ref={ref}
                label={label}
                fullWidth
                select
                margin={noMargin ? 'none' : 'normal'}
                SelectProps={{
                    open: isOpen,
                    onOpen: onOpen,
                    onClose: handleClose,
                    multiple: multiple,
                    displayEmpty: true,
                    value: values,
                    renderValue: renderValue ? renderValue : _renderValue,
                    onChange: onChange,
                    sx: (theme) => ({
                        [`& .${selectClasses.icon}`]: {
                            color: theme.bino.color.darkGray,
                            height: 8,
                            mt: 0.5,
                        },
                    }),
                    IconComponent: ArrowDownIcon,
                    MenuProps: {
                        ...MenuProps,
                        // Below props are just hack for avoiding the popover weird issue
                        // whenever we select an items. Right now, selecting an items
                        // make the menu jumps to the top.
                        // And also this is the solution to show the menu below the select
                        // which is better and natural.
                        // Check this issue - https://github.com/mui-org/material-ui/issues/12208
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        },
                    },
                }}
            >
                {children}
                {/*Does not quite work, since the Select assumes just MenuItems. */}
                {!hideCloseButton && (
                    <ListItem
                        sx={(theme) => ({
                            color: theme.bino.color.label,
                            textAlign: 'center',
                            display: 'flex',
                            justifyContent: 'space-evenly',
                            '&:focus': {
                                outline: 0,
                            },
                        })}
                    >
                        <MuiButton onClick={handleClose} fullWidth>
                            <FormattedMessage
                                defaultMessage="Close"
                                description="Close button"
                            />
                        </MuiButton>
                    </ListItem>
                )}
            </TextField>
        );
    },
);

export default MultipleSelect;
